import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNow } from 'date-fns';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function toDateObj(date: string | Date | null | undefined): Date | null {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date === 'number') return new Date(date);
  if (typeof date === 'object') {
    // Firestore Timestamp serialized as {seconds, nanoseconds} or {_seconds, _nanoseconds}
    const secs = (date as any).seconds ?? (date as any)._seconds;
    if (typeof secs === 'number') return new Date(secs * 1000);
    // Firestore Timestamp class instance with toDate()
    if (typeof (date as any).toDate === 'function') return (date as any).toDate();
  }
  if (typeof date === 'string') return new Date(date);
  return null;
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const dateObj = toDateObj(date);
  if (!dateObj || isNaN(dateObj.getTime())) return '—';
  return format(dateObj, 'MMM dd, yyyy HH:mm');
}

export function formatRelativeDate(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const dateObj = toDateObj(date);
  if (!dateObj || isNaN(dateObj.getTime())) return '—';
  return formatDistanceToNow(dateObj, { addSuffix: true });
}

export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export function getSubscriptionColor(tier: string): string {
  switch (tier) {
    case 'enterprise':
      return 'text-purple-600 bg-purple-100';
    case 'premium':
      return 'text-blue-600 bg-blue-100';
    case 'basic':
      return 'text-green-600 bg-green-100';
    case 'free':
      return 'text-gray-600 bg-gray-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'text-green-600 bg-green-100';
    case 'suspended':
      return 'text-red-600 bg-red-100';
    case 'pending':
      return 'text-yellow-600 bg-yellow-100';
    case 'cancelled':
      return 'text-gray-600 bg-gray-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

export function generateId(): string {
  return Math.random().toString(36).substr(2, 9);
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
} 